import React, { useEffect, useRef, useState } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import ProductGrid, { GRID_ITEMS_PER_PAGE } from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import { GridButtonGroupBase, GridView } from 'src/components/common/Buttons/GridButtonGroupBase';
import ProductExportModal from './ProductExportModal';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import BeaconProChart from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/BeaconProChart';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  ChartDataAdapter,
  Product,
  TableGridData
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import {
  BeaconProTable,
  Column
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/BeaconProTable/BeaconProTable';
import { useBeaconFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBeaconFilters';
import { SxProps } from '@mui/system';

interface ContentTableGridProps {
  title: string;
  queryFn: () => Promise<any>;
  columnDefinitions: Column<any>[];
  queryKeyOverride?: string;
  renderSubtitle: (view: 'table' | 'grid', data?: any) => string | React.ReactNode;
  productColumn?: Column<{ stacklineSku: string; product: Product }>;
  mode?: 'table' | 'grid' | 'both';
  tableSx?: SxProps;
}

export const ContentTableGrid = ({
  title,
  columnDefinitions,
  queryFn,
  queryKeyOverride,
  renderSubtitle,
  productColumn = null,
  mode = 'both',
  tableSx
}: ContentTableGridProps) => {
  const [gridView, setGridView] = useState(mode === 'grid' || mode === 'both' ? GridView.Tile : GridView.Table);
  const [page, setPage] = useState(1);

  const name = queryKeyOverride ?? `content-table-grid-${title.toLowerCase().replace(/\s+/g, '-')}`;
  const columns = productColumn ? [productColumn, ...columnDefinitions] : columnDefinitions;

  const { appFilters } = useBeaconFilters();
  const { categoryFilters, subcategoryFilters, businessUnitFilters, segmentFilters } = appFilters;

  const prevFiltersRef = useRef({
    categoryFilters: [],
    subcategoryFilters: [],
    businessUnitFilters: [],
    segmentFilters: []
  });

  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const currentFilters = {
      categoryFilters,
      subcategoryFilters,
      businessUnitFilters,
      segmentFilters
    };

    const filtersChanged = Object.keys(currentFilters).some(
      (key) => JSON.stringify(prevFilters[key]) !== JSON.stringify(currentFilters[key])
    );

    if (filtersChanged) {
      setPage(1);
      prevFiltersRef.current = currentFilters;
    }
  }, [categoryFilters, subcategoryFilters, businessUnitFilters, segmentFilters]);

  const [openExportModal, setOpenExportModal] = useState(false);

  return (
    <BeaconProChart queryFn={queryFn} name={name}>
      <ProductExportModal open={openExportModal} onClose={() => setOpenExportModal(false)} />
      <BeaconProChart.Header
        menuButton={() => (
          <GridButtonGroupBase
            icons={{
              tile: mode === 'both',
              table: mode === 'both',
              export: true
            }}
            selectedView={gridView}
            onChangeView={setGridView}
            onExport={() => {
              setOpenExportModal(true);
            }}
          />
        )}
      >
        <BeaconProChart.Title>
          <Text variant="h4">{title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(adapter) => {
          return renderSubtitle(gridView === GridView.Tile ? 'grid' : 'table', adapter?.transform());
        }}
      />

      <BeaconProChart.Visualization
        deferLoading
        render={(adapter: ChartDataAdapter<TableGridData>, isLoading) => {
          const data = adapter?.transform();
          const totalPages = Math.ceil(data?.length / GRID_ITEMS_PER_PAGE);

          const showPagination = totalPages > 1 && !isLoading;

          return (
            <Flex sx={{ marginTop: '32px' }} flexDirection="column" alignItems="flex-start">
              {gridView === GridView.Tile ? (
                <ProductGrid
                  fieldName={columnDefinitions[0]?.id}
                  loading={isLoading}
                  metricType={METRICTYPE.PERCENT}
                  page={page}
                  data={data ?? []}
                />
              ) : (
                <BeaconProTable
                  isLoading={isLoading}
                  data={data ?? []}
                  page={page}
                  columns={columns}
                  tableSx={tableSx}
                />
              )}
              {showPagination && (
                <div style={{ marginTop: '24px', alignSelf: 'flex-end' }}>
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onClickNext={() => setPage(Math.min(page + 1, totalPages))}
                    onClickPrevious={() => setPage(Math.max(page - 1, 1))}
                  />
                </div>
              )}
            </Flex>
          );
        }}
      />
    </BeaconProChart>
  );
};
